import { useRef, useState } from 'react';
import { BaseControl } from '.';
import saveFile from '../../../../hooks/SaveFile';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';

/**
 * @typedef {object} ImageControlOptions
 */

/**
 * @extends {BaseControl<string|null, ImageControlOptions>}
 */
export class ImageControl extends BaseControl {

    /** @type {BaseControl<string|null>['render']} */
    render(value, onChange) {
        return (
            <ImageControlRenderer
                control={this}
                value={value ?? null}
                onChange={onChange}
            />
        );
    }
}

/**
 * @param {object} props
 * @param {ImageControl} props.control
 * @param {string|null} props.value - Current image URL, or null
 * @param {(newValue: string|null) => void} props.onChange
 */
const ImageControlRenderer = ({ control, value, onChange }) => {
    
    const inputRef = useRef(/** @type {HTMLInputElement|null} */ (null))

    const [loading, setLoading] = useState(false);

    /** @param {React.ChangeEvent<HTMLInputElement>} e */
    const handleFileChange = e => {
        if (!e.target.files?.length) return;
        setLoading(true);
        saveFile(
            e,
            /** @param {string} url */
            url => { onChange(url); setLoading(false); },
            null,
            /** @param {number} progress */
            progress => setLoading(progress < 100)
        );
        /* Reset so the same file can be re-selected */
        e.target.value = '';
    };

    return (
        <div className="image-control">
            <label className="image-control-label">{control.label}</label>

            <div
                className={`image-control-preview${value ? '' : ' image-control-preview--empty'}`}
                onClick={() => !value && inputRef.current?.click()}
                title={value ? undefined : 'Afbeelding toevoegen'}
            >
                {value ? (
                    <>
                        <img src={value} alt="Huidige afbeelding" />
                        <div className="image-control-overlay">
                            <button
                                type="button"
                                title="Afbeelding wijzigen"
                                onClick={e => {
                                    e.stopPropagation();
                                    inputRef.current?.click();
                                }}
                            >
                                <EditIcon fontSize="small" />
                            </button>
                            <button
                                type="button"
                                title="Afbeelding verwijderen"
                                onClick={e => {
                                    e.stopPropagation();
                                    onChange(null);
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </button>
                        </div>
                    </>
                ) : (
                    <span className="image-control-empty-icon">
                        <AddPhotoAlternateIcon fontSize="small" />
                    </span>
                )}

                {loading && (
                    <div className="image-control-loading">Laden...</div>
                )}
            </div>

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
        </div>
    );
};

export default ImageControl;
